import { createContext, use, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { notifications } from '@mantine/notifications';
import { useMutation, useSubscription } from 'urql';
import type { OrgRole } from '@shared';
import { graphql } from '../lib/graphql';
import { getErrorMessage } from '../lib/error-message';
import { useAuth } from './auth';
import { useMeOptional } from '../hooks/use-me';

const MyMembershipsSubscription = graphql(`
  subscription MyMemberships($userId: Uuid!) {
    organizationMembers(
      where: { userId: { _eq: $userId }, status: { _eq: "active" } }
      orderBy: { organization: { name: ASC } }
    ) {
      id
      role
      organization {
        id
        name
        slug
      }
    }
  }
`);

const MyCurrentOrganizationSubscription = graphql(`
  subscription MyCurrentOrganization($userId: Uuid!) {
    usersByPk(id: $userId) {
      id
      currentOrganizationId
    }
  }
`);

const AllOrganizationsSubscription = graphql(`
  subscription AllOrganizations {
    organizations(orderBy: { name: ASC }) {
      id
      name
      slug
    }
  }
`);

const SetCurrentOrganizationMutation = graphql(`
  mutation SetCurrentOrganization($userId: Uuid!, $organizationId: Uuid!) {
    updateUsersByPk(pkColumns: { id: $userId }, _set: { currentOrganizationId: $organizationId }) {
      id
      currentOrganizationId
    }
  }
`);

export type Org = {
  id: string;
  name: string;
  slug: string;
  role: OrgRole | null;
};

type OrgState = {
  organizations: Org[];
  platformOrganizations: Org[];
  currentOrg: Org | null;
  organizationId: string | null;
  isPlatformOperatorView: boolean;
  isLoading: boolean;
  setCurrentOrg: (organizationId: string) => Promise<void>;
};

const OrgContext = createContext<OrgState | null>(null);

function toRole(role: string): OrgRole {
  return role as OrgRole;
}

export function OrgProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const { me, isSuperUser } = useMeOptional();
  const userId = me?.id ?? null;
  const paused = !userId || !token;

  const [memberships] = useSubscription({
    query: MyMembershipsSubscription,
    variables: { userId: userId ?? '' },
    pause: paused,
  });

  const [currentSelection] = useSubscription({
    query: MyCurrentOrganizationSubscription,
    variables: { userId: userId ?? '' },
    pause: paused,
  });

  const [allOrganizations] = useSubscription({
    query: AllOrganizationsSubscription,
    pause: paused || !isSuperUser,
  });

  const [, runSetCurrentOrganization] = useMutation(SetCurrentOrganizationMutation);

  const organizations = useMemo<Org[]>(
    () =>
      (memberships.data?.organizationMembers ?? []).map((m) => ({
        id: m.organization.id,
        name: m.organization.name,
        slug: m.organization.slug,
        role: toRole(m.role),
      })),
    [memberships.data],
  );

  const platformOrganizations = useMemo<Org[]>(() => {
    if (!isSuperUser) return [];

    const memberOrgIds = new Set(organizations.map((o) => o.id));

    return (allOrganizations.data?.organizations ?? [])
      .filter((o) => !memberOrgIds.has(o.id))
      .map((o) => ({ id: o.id, name: o.name, slug: o.slug, role: null }));
  }, [isSuperUser, allOrganizations.data, organizations]);

  const currentOrg = useMemo(() => {
    const selectedId = currentSelection.data?.usersByPk?.currentOrganizationId ?? null;
    const selected = selectedId
      ? (organizations.find((o) => o.id === selectedId) ?? platformOrganizations.find((o) => o.id === selectedId))
      : undefined;

    return selected ?? organizations[0] ?? null;
  }, [currentSelection.data, organizations, platformOrganizations]);

  const setCurrentOrg = useCallback(
    async (organizationId: string) => {
      if (!userId) return;

      const result = await runSetCurrentOrganization({ userId, organizationId });

      if (result.error) {
        notifications.show({
          color: 'red',
          title: 'Unable to switch organization',
          message: getErrorMessage(result.error, 'Something went wrong switching organizations'),
        });
      }
    },
    [userId, runSetCurrentOrganization],
  );

  const value: OrgState = {
    organizations,
    platformOrganizations,
    currentOrg,
    organizationId: currentOrg?.id ?? null,
    isPlatformOperatorView: currentOrg !== null && currentOrg.role === null,
    isLoading: !paused && (memberships.data === undefined || currentSelection.data === undefined),
    setCurrentOrg,
  };

  return <OrgContext value={value}>{children}</OrgContext>;
}

export function useOrgContext() {
  const org = use(OrgContext);

  if (!org) throw new Error('useOrg must be used within OrgProvider');

  return org;
}
